// @flow

import queryString from 'query-string';
import React, { useRef } from 'react';
import type { Node } from 'react';
import { useIntl, FormattedMessage, FormattedDate } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import { Section, Wrapper, FadeContent } from '@riseart/layout';
import {
  SliderPagination,
  Link,
  LazyloadPlaceholder,
  scrollToElement,
  MediaQuery,
} from '@riseart/common';
import { CardArticle } from '@riseart/article';
import { GridHorizontal, GridHorizontalItem, GridPaginationWrapper } from '@riseart/grid';
import {
  article as ENUM_ARTICLE,
  pagination as ENUM_PAGINATION,
  HTTP_STATUS,
  meta as META_ENUM,
} from 'Enum';
import { useQuery } from 'shared_services/apollo/useQuery';
import { gql404RedirectHandler } from 'shared_services/riseart/utils/RouteUtils';
import { useUrlParams } from 'shared_data/providers/url/useUrlParams';
import { LayoutDefault } from 'shared_components/layouts/Default';
import { Breadcrumbs } from 'shared_components/common/breadcrumbs/Breadcrumbs';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import {
  generateSrcForCDN,
  PicturePlaceholder,
} from 'shared_components/common/artdirection/picture/Picture';
import { extractImageFileFromData } from 'shared_services/riseart/utils/ImageUtils';
import { Pagination } from 'shared_hocs/pagination/Pagination';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { BlogHeader } from 'shared_components/article/category/Header';
import { DataProviderPreloaderPage } from 'shared_components/data/providers/preloaders/Page';
import CATEGORY_PAGE_QUERY from 'shared_data/queries/articles/filterCategoryAndArticles.graphql';
import { MetaProvider } from 'shared_data/providers/meta/Meta';
import { LDTYPES, generateLDJSON } from 'shared_models/seo/ldJSON';
import { generateHrefLangParams } from 'shared_services/riseart/meta/utils';
import { LocationManager } from 'shared_services/riseart/url/Location';
import { Share } from 'shared_components/common/share/Share';

import { raScreenSm } from '@riseart/antd-provider/dist/website/variables.less';

const LAZYLOAD_ARTICLE_CONFIG = {
  once: true,
  height: 175,
  offset: 200,
  placeholder: <LazyloadPlaceholder height={175} />,
};

/**
 * CommonCategoryPage
 *
 * @returns
 */
export function CommonCategoryPage({
  staticContext,
  articleType,
  showOnlyShowcaseItems,
  translationPrefix,
  listTitleTranslationKey,
  metaSubscriber,
  mainQueryPreloaderId,
  customHeaderMenu = null,
  showDate = true,
  listUrl,
}: Object): Node {
  const ITEMS_PER_PAGE = 10;
  const { formatMessage } = useIntl();
  const history = useHistory();
  const { translatedLocation: location } = useUrlParams();
  const topOfGridRef = useRef(null);
  const { category: selectedCategoryUri } = useParams();
  const { page = 1 } = queryString.parse(location.search);
  const scrollToTopOfGrid = () => scrollToElement(topOfGridRef);
  const { loading, data, error } = useQuery(CATEGORY_PAGE_QUERY, {
    variables: {
      categoryId: selectedCategoryUri,
      inputArticleCategoryFilter: {
        page: 1,
        items: 6,
        type: articleType,
      },
      inputArticleFilter: {
        category: selectedCategoryUri,
        page,
        items: ITEMS_PER_PAGE,
        type: articleType,
        ...(showOnlyShowcaseItems ? { showcase: 1 } : {}),
      },
    },
    context: {
      customOptions: {
        errorSuppressFromResponse: true,
        errorHandler: gql404RedirectHandler(
          UrlAssembler.articleList(articleType, {
            search: { message: HTTP_STATUS.NOT_FOUND },
          }),
          history,
          staticContext,
        ),
      },
    },
  });
  const origin = LocationManager.get('origin');
  const {
    category,
    categories: { items: categories } = {},
    articles: { items: articles, pagination = {} } = {},
  } = data || {};
  const hasArticles = articles && articles.length;
  const canonicalURL = category
    ? UrlAssembler.articleCategory(category.uri, category.type, { origin })
    : LocationManager.get('originWithPath');

  const metaData = category
    ? {
        title:
          category.metaTitle ||
          formatMessage(
            { id: `meta.${translationPrefix}.dynamicMetaTitle` },
            { title: category.name },
          ),
        description:
          category.metaDescription ||
          formatMessage(
            { id: `meta.${translationPrefix}.dynamicMetaDescription` },
            { title: category.name },
          ),
        keywords:
          category.metaKeywords ||
          formatMessage(
            { id: `meta.${translationPrefix}.dynamicMetaKeywords` },
            {
              title: category.name,
            },
          ),
        hrefLangParams: generateHrefLangParams(category.locales, {
          uri: 'category',
        }),
      }
    : {};

  return (
    <LayoutDefault scrollTopOnPathChange>
      <IsomorphicRipple isActive={loading}>
        <DataProviderPreloaderPage uniqueId={mainQueryPreloaderId} attach={loading} />
        {!loading ? (
          <MetaProvider
            metaSubscriber={metaSubscriber}
            meta={
              data
                ? {
                    [META_ENUM.METATYPE.LINK_CANONICAL]: canonicalURL,
                    ...(category
                      ? {
                          [META_ENUM.METATYPE.LINK_CANONICAL_REDIRECT]:
                            UrlAssembler.articleCategory(category.uri, category.type, {
                              ...(location.search ? { search: location.search } : null),
                              ...(location.hash ? { hash: location.hash } : null),
                            }),
                        }
                      : null),
                    [META_ENUM.METATYPE.LINK_HREFLANG]: metaData.hrefLangParams,
                    [META_ENUM.METATYPE.LINK_HREFLANG_REDIRECT]: metaData.hrefLangParams,
                    [META_ENUM.METATYPE.META_TITLE]: metaData.title,
                    [META_ENUM.METATYPE.META_KEYWORDS]: metaData.keywords,
                    [META_ENUM.METATYPE.META_DESCRIPTION]: metaData.description,
                    [META_ENUM.METATYPE.META_OG_TITLE]: metaData.title,
                    [META_ENUM.METATYPE.META_OG_DESCRIPTION]: metaData.description,
                    [META_ENUM.METATYPE.META_OG_URL]: canonicalURL,
                    [META_ENUM.METATYPE.SCRIPT_LD_JSON_ARTICLES_LIST]: hasArticles
                      ? generateLDJSON(
                          LDTYPES.CAROUSEL_ARTICLES,
                          articles.map((item) => {
                            const image =
                              (item.images &&
                                extractImageFileFromData.articleImage(
                                  item.images,
                                  ENUM_ARTICLE.image.type.TYPE_MAIN_MASTER,
                                )) ||
                              null;
                            return {
                              ...item,
                              author: {
                                fullName: item.author.fullName,
                                url:
                                  item && item.author
                                    ? UrlAssembler.authorDetail(
                                        item.author.type,
                                        item.author.typeId,
                                        item.author.typeAlias,
                                        { origin },
                                      )
                                    : null,
                              },
                              image: image
                                ? generateSrcForCDN({
                                    ...image,
                                    width: 1200,
                                  })
                                : null,
                              mainEntityOfPage: {
                                type: 'WebPage',
                                url: UrlAssembler.articleDetail(item.id, item.slug, item.type, {
                                  origin,
                                }),
                              },
                            };
                          }),
                        )
                      : null,
                  }
                : null
            }
            hasError={!!error || !data}
          />
        ) : null}
        <Section padding="none">
          <Wrapper>
            <Breadcrumbs
              links={[
                {
                  isLink: true,
                  name: formatMessage({ id: listTitleTranslationKey }),
                  url: listUrl,
                },
                ...(category
                  ? [
                      {
                        isLink: false,
                        name: category.name,
                      },
                    ]
                  : []),
              ]}
            />
            <BlogHeader
              title={category && category.name}
              description={category && category.description}
              categories={categories}
              selectedCategoryId={category && category.id}
            >
              {customHeaderMenu ? customHeaderMenu(categories, category && category.id) : null}
            </BlogHeader>
          </Wrapper>
        </Section>
        <Section>
          <Wrapper
            sizes={{
              xs: 'wide',
              sm: 'wide',
              md: 'wide',
              lg: 'narrow',
              xl: 'narrow',
            }}
          >
            <div ref={topOfGridRef} />
            <MediaQuery minWidth={raScreenSm}>
              {(isTabletOrDesktop) => (
                <GridHorizontal columnCount="1">
                  {hasArticles
                    ? articles.map(
                        (
                          {
                            id,
                            type,
                            slug,
                            title,
                            summary,
                            publishedDate,
                            images,
                            author,
                            categories,
                          }: Object,
                          idx: number,
                        ) => {
                          const articleUrl = UrlAssembler.articleDetail(id, slug, type);
                          const authorUrl = author
                            ? UrlAssembler.authorDetail(
                                author.type,
                                author.typeId,
                                author.typeAlias,
                              )
                            : null;

                          let displayStyle =
                            !isTabletOrDesktop && [0, 6].indexOf(idx) !== -1 ? 2 : 1;

                          if (!isTabletOrDesktop && [0, 6].indexOf(idx) === -1) {
                            displayStyle = '3reversed';
                          }

                          return (
                            <GridHorizontalItem
                              key={`${(category && category.id) || ''}_${id}_${slug || ''}`}
                              columnCount="1"
                            >
                              <CardArticle
                                displayStyle={displayStyle}
                                title={
                                  <Link to={articleUrl} title={title}>
                                    {title}
                                  </Link>
                                }
                                subtitle={
                                  !isTabletOrDesktop && idx === 0 && categories && categories[0] ? (
                                    <Link
                                      to={UrlAssembler.articleCategory(
                                        categories[0].uri,
                                        categories[0].type,
                                      )}
                                      title={categories[0].name}
                                    >
                                      {categories[0].name}
                                    </Link>
                                  ) : null
                                }
                                description={
                                  isTabletOrDesktop ? (
                                    <FadeContent height="80px" maxHeight={null}>
                                      {summary}
                                    </FadeContent>
                                  ) : null
                                }
                                date={
                                  showDate && (isTabletOrDesktop || displayStyle !== 1) ? (
                                    <FormattedDate
                                      value={new Date(publishedDate)}
                                      year="numeric"
                                      month="short"
                                      day="2-digit"
                                    >
                                      {(dateLabel: string) => dateLabel}
                                    </FormattedDate>
                                  ) : null
                                }
                                author={
                                  isTabletOrDesktop || displayStyle !== 1 ? (
                                    <FormattedMessage
                                      id="common.byName"
                                      values={{
                                        name: authorUrl ? (
                                          <Link to={authorUrl} title={author.fullName}>
                                            {author.fullName}
                                          </Link>
                                        ) : (
                                          author.fullName
                                        ),
                                      }}
                                    />
                                  ) : null
                                }
                                share={
                                  <Share
                                    theme="dark"
                                    contentKey="article"
                                    contentValues={{ title }}
                                    image={generateSrcForCDN(
                                      extractImageFileFromData.articleImage(images),
                                    )}
                                    url={`${LocationManager.get('origin')}${articleUrl}`}
                                    buttonText={
                                      isTabletOrDesktop ? (
                                        <FormattedMessage id="common.share" />
                                      ) : null
                                    }
                                  />
                                }
                              >
                                <Link to={articleUrl} title={title}>
                                  <PicturePlaceholder
                                    type={
                                      idx === 0
                                        ? 'blog.singleCardFeatured'
                                        : 'blog.singleCardAdditional'
                                    }
                                    artDirectionKey="article.main"
                                    image={extractImageFileFromData.articleImage(images)}
                                    alt={title}
                                    // Do not lazyload the first item image
                                    lazyload={idx ? LAZYLOAD_ARTICLE_CONFIG : null}
                                  />
                                </Link>
                              </CardArticle>
                            </GridHorizontalItem>
                          );
                        },
                      )
                    : null}
                </GridHorizontal>
              )}
            </MediaQuery>
            {hasArticles ? (
              <GridPaginationWrapper>
                <Pagination
                  paginationType={ENUM_PAGINATION.type.SLIDER}
                  totalPages={pagination.totalPages}
                  total={pagination.totalItems}
                  pageSize={pagination.itemsPerPage}
                  current={pagination.currentPage}
                  beforeChange={scrollToTopOfGrid}
                >
                  {(props) => <SliderPagination {...props} />}
                </Pagination>
              </GridPaginationWrapper>
            ) : null}
          </Wrapper>
        </Section>
      </IsomorphicRipple>
    </LayoutDefault>
  );
}
